import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trash2, RotateCcw, Loader2, Clock, XCircle, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-US', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const hoursLeft = (expiresAt) => {
    const ms = new Date(expiresAt) - Date.now();
    if (ms <= 0) return 'expira ahora';
    const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

export default function Trash() {
    const { getAuthHeaders } = useAuth();
    const [data, setData] = useState({ items: [], count: 0, total_amount: 0, ttl_hours: 24 });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/trash`, { headers: getAuthHeaders() });
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cargar la papelera');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => { load(); }, [load]);

    const restore = async (item) => {
        setBusy(item.invoice_id);
        try {
            await axios.post(`${API}/api/invoices/${item.invoice_id}/restore`, {}, { headers: getAuthHeaders() });
            toast.success(`Factura recuperada: ${item.name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo recuperar');
        } finally {
            setBusy(null);
        }
    };

    const purge = async (item) => {
        if (!window.confirm(`¿Eliminar definitivamente "${item.name}" (${money(item.amount)})? Esta acción no se puede deshacer.`)) return;
        setBusy(item.invoice_id);
        try {
            await axios.delete(`${API}/api/admin/trash/${item.invoice_id}`, { headers: getAuthHeaders() });
            toast.success('Eliminada definitivamente');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo eliminar');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="trash-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800 font-['Manrope'] flex items-center gap-3">
                        <span className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center"><Trash2 className="w-5 h-5 text-white" /></span>
                        Papelera
                    </h1>
                    <p className="text-slate-500 mt-1">Facturas eliminadas en las últimas {data.ttl_hours} horas. Pasado ese tiempo se borran definitivamente.</p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="trash-refresh-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
                </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md">
                <Card className="border-0 shadow-sm"><CardContent className="p-4">
                    <p className="text-xs text-slate-500">En papelera</p>
                    <p className="text-2xl font-bold text-slate-800" data-testid="trash-count">{data.count}</p>
                </CardContent></Card>
                <Card className="border-0 shadow-sm"><CardContent className="p-4">
                    <p className="text-xs text-slate-500">Monto total</p>
                    <p className="text-2xl font-bold text-red-600" data-testid="trash-total">{money(data.total_amount)}</p>
                </CardContent></Card>
            </div>

            <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
                    ) : data.items.length === 0 ? (
                        <div className="py-16 text-center text-slate-500" data-testid="trash-empty">
                            <Trash2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            La papelera está vacía
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-slate-500 border-b bg-slate-50">
                                        <th className="py-3 px-4">Eliminada</th><th className="py-3 px-4">Factura</th><th className="py-3 px-4">Usuario</th>
                                        <th className="py-3 px-4 text-right">Monto</th><th className="py-3 px-4">Estado</th><th className="py-3 px-4">Eliminó</th>
                                        <th className="py-3 px-4">Tiempo restante</th><th className="py-3 px-4 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((it) => (
                                        <tr key={it.invoice_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`trash-item-${it.invoice_id}`}>
                                            <td className="py-3 px-4 text-slate-500 whitespace-nowrap">{fmt(it.deleted_at)}</td>
                                            <td className="py-3 px-4 font-medium">
                                                <span className="flex items-center gap-2">
                                                    {it.name}
                                                    {it.category && <span className="text-xs bg-slate-100 text-slate-600 px-1.5 rounded">{it.category}</span>}
                                                    {it.is_extra && <span className="text-xs bg-fuchsia-100 text-fuchsia-700 px-1.5 rounded inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />Extra</span>}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">{it.user_name}</td>
                                            <td className="py-3 px-4 text-right font-semibold whitespace-nowrap">{money(it.amount)}</td>
                                            <td className="py-3 px-4">
                                                {it.confirmed
                                                    ? <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Confirmada</span>
                                                    : <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Pendiente</span>}
                                            </td>
                                            <td className="py-3 px-4 text-slate-600">{it.deleted_by_name}</td>
                                            <td className="py-3 px-4 text-slate-500 whitespace-nowrap"><span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{hoursLeft(it.expires_at)}</span></td>
                                            <td className="py-3 px-4 text-right whitespace-nowrap">
                                                <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white mr-2" disabled={busy === it.invoice_id} onClick={() => restore(it)} data-testid={`trash-restore-${it.invoice_id}`}>
                                                    <RotateCcw className="w-4 h-4 mr-1" /> Recuperar
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={busy === it.invoice_id} onClick={() => purge(it)} data-testid={`trash-purge-${it.invoice_id}`}>
                                                    <XCircle className="w-4 h-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
